import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Layers,
  XCircle,
  Check,
  Trash2,
  RefreshCw,
  Banknote,
  Download,
  ChevronDown,
  RotateCcw,
} from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { PermissionGate } from '@/components/common/PermissionGate'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  useBatch,
  useBatchSummary,
  useBatchItems,
  useBatchReadiness,
  useConsolidateBatch,
  useApproveBatch,
  useMarkPaid,
  useRejectBatch,
  useResubmitBatch,
  useVoidBatch,
  useReverseBatchPayment,
} from '@/features/payroll/hooks/usePayrollBatches'
import {
  downloadBatchPreview,
  downloadBatchSlipsZip,
} from '@/api/services/payroll-batches.service'
import { triggerBlobDownload } from '@/utils/download'
import { StatusBadge } from './StatusBadge'
import { StatCard } from './StatCard'
import { RejectionInfo } from './RejectionInfo'
import { RejectionHistoryTimeline } from './RejectionHistoryTimeline'
import { RejectReasonModal } from './RejectReasonModal'
import type { ConsolidatedPayrollItem } from '@/features/payroll/types/payroll.types'

function getEmployeeLabel(emp: ConsolidatedPayrollItem['employeeId']): { name: string; code?: string } {
  if (typeof emp === 'string') return { name: emp }
  const name = [emp.firstName, emp.lastName].filter(Boolean).join(' ') || emp.email || emp._id
  return { name, code: emp.employeeId }
}

function getEmployeeRawId(emp: ConsolidatedPayrollItem['employeeId']): string {
  return typeof emp === 'string' ? emp : emp._id
}

export function BatchDetailPage(): React.ReactElement {
  const { batchId } = useParams<{ batchId: string }>()
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [confirmAction, setConfirmAction] = useState<
    'consolidate' | 'approve' | 'markPaid' | 'void' | null
  >(null)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [reverseOpen, setReverseOpen] = useState(false)
  const [resubmitOpen, setResubmitOpen] = useState(false)
  const [markPaidChecked, setMarkPaidChecked] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadingZip, setDownloadingZip] = useState(false)

  const { data: batch, isLoading, isError } = useBatch(batchId ?? null)
  const { data: summary } = useBatchSummary(batchId ?? null)
  const { data: itemsData, isLoading: itemsLoading } = useBatchItems(batchId ?? null, page)

  // Readiness loaded lazily when resubmit modal opens
  const { data: readiness, isLoading: readinessLoading } = useBatchReadiness(
    batch?.payrollMonth.year ?? 2000,
    batch?.payrollMonth.month ?? 1,
    { enabled: resubmitOpen && !!batch },
  )

  const consolidateMut = useConsolidateBatch()
  const approveMut = useApproveBatch()
  const markPaidMut = useMarkPaid()
  const rejectMut = useRejectBatch()
  const resubmitMut = useResubmitBatch()
  const voidMut = useVoidBatch()
  const reverseMut = useReverseBatchPayment()

  const anyMutating =
    consolidateMut.isPending ||
    approveMut.isPending ||
    markPaidMut.isPending ||
    rejectMut.isPending ||
    resubmitMut.isPending ||
    voidMut.isPending ||
    reverseMut.isPending

  if (!batchId) {
    navigate('/payroll', { replace: true })
    return <></>
  }

  if (isLoading) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'Payroll', to: '/payroll' }, { label: 'Batch' }]}
        icon={Layers}
        title="Payroll Batch"
        className="gap-0"
        hideHeader
      >
        <TableSkeleton rows={5} cols={5} />
      </PageShell>
    )
  }

  if (isError || !batch) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'Payroll', to: '/payroll' }, { label: 'Not Found' }]}
        icon={Layers}
        title="Payroll Batch"
        className="gap-0"
        hideHeader
      >
        <EmptyState icon={XCircle} title="Batch not found" />
      </PageShell>
    )
  }

  const period = `${batch.payrollMonth.year}-${String(batch.payrollMonth.month).padStart(2, '0')}`
  const status = batch.status
  const items = itemsData?.data ?? []
  const orgTotals = summary?.orgTotals

  const handleConfirm = (): void => {
    if (!confirmAction) return
    const id = batchId
    const close = (): void => {
      setConfirmAction(null)
      setMarkPaidChecked(false)
    }
    if (confirmAction === 'consolidate') consolidateMut.mutate(id, { onSuccess: close })
    if (confirmAction === 'approve') approveMut.mutate(id, { onSuccess: close })
    if (confirmAction === 'markPaid') markPaidMut.mutate(id, { onSuccess: close })
    if (confirmAction === 'void') voidMut.mutate(id, { onSuccess: close })
  }

  const handleReject = (reason: string): void => {
    rejectMut.mutate({ batchId, reason }, { onSuccess: () => setRejectOpen(false) })
  }

  const handleReverse = (reason: string): void => {
    reverseMut.mutate({ batchId, reason }, { onSuccess: () => setReverseOpen(false) })
  }

  const handleResubmit = (): void => {
    resubmitMut.mutate(batchId, { onSuccess: () => setResubmitOpen(false) })
  }

  const handleDownload = async (format: 'pdf' | 'csv'): Promise<void> => {
    setDownloading(true)
    try {
      const blob = await downloadBatchPreview(batchId, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `batch-${period}.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadSlipsZip = async (): Promise<void> => {
    setDownloadingZip(true)
    try {
      const blob = await downloadBatchSlipsZip(batchId)
      triggerBlobDownload(blob, `salary-slips-${period}.zip`)
    } finally {
      setDownloadingZip(false)
    }
  }

  const readinessApproved = readiness?.filter((e) => e.runStatus === 'approved').length ?? 0
  const readinessTotal = readiness?.length ?? 0
  const allReady = readinessTotal > 0 && readinessApproved === readinessTotal

  const confirmLabels: Record<
    string,
    { title: string; message: string; label: string }
  > = {
    consolidate: {
      title: 'Consolidate Batch',
      message:
        'This will combine all approved company runs, re-calculate tax on consolidated amounts, and generate consolidated payroll items.',
      label: 'Consolidate',
    },
    approve: {
      title: 'Approve Batch',
      message: 'This batch will be marked as approved and ready for payment.',
      label: 'Approve',
    },
    markPaid: {
      title: 'Mark as Paid',
      message:
        'This will lock all source records (attendance, bonuses, penalties, security deposits) and notify all employees that their salary slips are available. This action cannot be easily undone.',
      label: 'Mark as Paid',
    },
    void: {
      title: 'Void Batch',
      message:
        'This will void the batch, delete all consolidated items, and unlock all source records.',
      label: 'Void Batch',
    },
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Payroll', to: '/payroll' },
        { label: `Batch ${period}` },
      ]}
      icon={Layers}
      title={`Payroll Batch — ${period}`}
      description={`${batch.currency} · ${status}`}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar actions={<StatusBadge status={status} />} />

        {/* Rejection info */}
        {batch.rejectedReason && (
          <RejectionInfo reason={batch.rejectedReason} rejectedAt={batch.rejectedAt} />
        )}

        {/* Payment reversal info */}
        {batch.reversalReason && (
          <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            <RotateCcw className="mt-0.5 h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" />
            <div className="min-w-0 text-sm">
              <p className="font-medium text-gray-800 dark:text-gray-200">Payment reversed</p>
              <p className="mt-1 text-gray-700 dark:text-gray-300">{batch.reversalReason}</p>
              {batch.reversedAt && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {new Date(batch.reversedAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Rejection history */}
        {batch.rejectionHistory.length > 0 && (
          <RejectionHistoryTimeline history={batch.rejectionHistory} />
        )}

        {/* Totals */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label="Gross Pay"
            value={orgTotals?.totalGross ?? batch.totals?.grossPay ?? 0}
            currency={batch.currency}
          />
          <StatCard
            label="Total Tax"
            value={orgTotals?.totalTax ?? batch.totals?.totalTax ?? 0}
            currency={batch.currency}
          />
          <StatCard
            label="Net Pay"
            value={orgTotals?.totalNet ?? batch.totals?.netPay ?? 0}
            currency={batch.currency}
          />
          <StatCard
            label="Employees"
            value={orgTotals?.headcount ?? batch.totals?.totalEmployees ?? 0}
          />
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
          {/* Consolidate */}
          <PermissionGate module="payroll" action="run">
            {(status === 'pending' || status === 'consolidated') && (
              <Button
                variant="primary"
                size="sm"
                disabled={anyMutating}
                onClick={() => setConfirmAction('consolidate')}
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                {status === 'consolidated' ? 'Re-consolidate' : 'Consolidate'}
              </Button>
            )}
          </PermissionGate>

          {/* Approve */}
          <PermissionGate module="payroll" action="finalize">
            {status === 'consolidated' && (
              <Button
                variant="primary"
                size="sm"
                disabled={anyMutating}
                onClick={() => setConfirmAction('approve')}
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Approve
              </Button>
            )}
          </PermissionGate>

          {/* Reject: finalizers decide batches, and rejecting also needs reject */}
          <PermissionGate module="payroll" action="finalize">
            <PermissionGate module="payroll" action="reject">
              {(status === 'pending' || status === 'consolidated') && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={anyMutating}
                  onClick={() => setRejectOpen(true)}
                >
                  <XCircle className="mr-1.5 h-3.5 w-3.5" />
                  Reject
                </Button>
              )}
            </PermissionGate>
          </PermissionGate>

          {/* Mark Paid */}
          <PermissionGate module="payroll" action="finalize">
            {status === 'approved' && (
              <Button
                variant="primary"
                size="sm"
                disabled={anyMutating}
                onClick={() => setConfirmAction('markPaid')}
              >
                <Banknote className="mr-1.5 h-3.5 w-3.5" />
                Mark as Paid
              </Button>
            )}
          </PermissionGate>

          {/* Void */}
          <PermissionGate module="payroll" action="finalize">
            {status === 'approved' && (
              <Button
                variant="danger"
                size="sm"
                disabled={anyMutating}
                onClick={() => setConfirmAction('void')}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Void
              </Button>
            )}
          </PermissionGate>

          {/* Reverse payment (a paid batch can no longer be voided) */}
          <PermissionGate module="payroll" action="finalize">
            {status === 'paid' && (
              <Button
                variant="danger"
                size="sm"
                disabled={anyMutating}
                onClick={() => setReverseOpen(true)}
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Reverse payment
              </Button>
            )}
          </PermissionGate>

          {/* Resubmit */}
          <PermissionGate module="payroll" action="run">
            {status === 'rejected' && (
              <Button
                variant="primary"
                size="sm"
                disabled={anyMutating}
                onClick={() => setResubmitOpen(true)}
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Resubmit
              </Button>
            )}
          </PermissionGate>

          {/* Preview / Export */}
          {status !== 'pending' && status !== 'rejected' && (
            <div className="ml-auto flex items-center gap-1">
              <Button
                variant="secondary"
                size="sm"
                disabled={downloading}
                onClick={() => handleDownload('pdf')}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                {downloading ? '...' : 'PDF'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={downloading}
                onClick={() => handleDownload('csv')}
              >
                <ChevronDown className="mr-1.5 h-3.5 w-3.5" />
                CSV
              </Button>
              <PermissionGate module="payroll" action="export">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={downloadingZip}
                  onClick={() => void handleDownloadSlipsZip()}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  {downloadingZip ? 'Preparing…' : 'All Slips (ZIP)'}
                </Button>
              </PermissionGate>
            </div>
          )}
        </div>

        {/* Company summary table */}
        {summary && summary.byCompany.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              By Company
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3 text-right">Headcount</th>
                    <th className="px-4 py-3 text-right">Gross</th>
                    <th className="px-4 py-3 text-right">Tax</th>
                    <th className="px-4 py-3 text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byCompany.map((co) => (
                    <tr
                      key={co.companyId}
                      className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/30"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {co.companyName}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{co.headcount}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {co.totalGross.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {co.totalTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">
                        {co.totalNet.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Consolidated items table */}
        <div>
          <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
            Consolidated Employee Items
          </h3>
          {itemsLoading ? (
            <TableSkeleton rows={5} cols={5} />
          ) : items.length === 0 ? (
            <EmptyState
              icon={Layers}
              title="No consolidated items yet"
              description="Run consolidation to generate employee data."
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      <th className="px-4 py-3">Employee</th>
                      <th className="px-4 py-3 text-right">Gross</th>
                      <th className="px-4 py-3 text-right">Tax</th>
                      <th className="px-4 py-3 text-right">Net</th>
                      <th className="px-4 py-3 text-center">Companies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item: ConsolidatedPayrollItem) => (
                      <tr
                        key={item._id}
                        className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/30"
                        onClick={() =>
                          navigate(`/payroll/batches/${batchId}/items/${getEmployeeRawId(item.employeeId)}`)
                        }
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {getEmployeeLabel(item.employeeId).name}
                          </div>
                          {getEmployeeLabel(item.employeeId).code && (
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              {getEmployeeLabel(item.employeeId).code}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {item.totals.combinedGross.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {item.totals.combinedTax.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium">
                          {item.totals.combinedNet.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-4 py-3 text-center tabular-nums">
                          {item.companyBreakdown.length}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {itemsData && itemsData.total > itemsData.limit && (
                <TablePaginationBar
                  page={page}
                  limit={itemsData.limit}
                  total={itemsData.total}
                  totalPages={Math.ceil(itemsData.total / itemsData.limit)}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirm dialog */}
      {confirmAction && confirmAction !== 'markPaid' && (
        <ConfirmDialog
          open
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
          title={confirmLabels[confirmAction].title}
          message={confirmLabels[confirmAction].message}
          confirmLabel={confirmLabels[confirmAction].label}
          variant={confirmAction === 'void' ? 'danger' : 'default'}
          confirmLoading={anyMutating}
        />
      )}

      {/* Mark Paid — extra-danger confirmation */}
      {confirmAction === 'markPaid' && (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirmAction(null)
            setMarkPaidChecked(false)
          }}
          onConfirm={handleConfirm}
          title="Mark as Paid"
          message="This locks source records and notifies employees. This cannot be easily undone."
          confirmLabel={markPaidChecked ? 'Mark as Paid' : 'Confirm below first'}
          variant="danger"
          confirmLoading={anyMutating}
        />
      )}

      {/* Reject modal */}
      <RejectReasonModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSubmit={handleReject}
        title="Reject Batch"
        description="This will revert all company runs to 'calculated' status. Company admins will need to re-approve before resubmission."
        loading={rejectMut.isPending}
      />

      {/* Reverse payment modal */}
      <RejectReasonModal
        open={reverseOpen}
        onClose={() => setReverseOpen(false)}
        onSubmit={handleReverse}
        title="Reverse payment"
        description="Records that this payment was reversed, returns the company runs to approved and reopens the month so attendance, leave and pay can change again. Employees are not notified. Say why (for example, paid to the wrong account)."
        submitLabel="Reverse payment"
        loading={reverseMut.isPending}
      />

      {/* Resubmit modal */}
      {resubmitOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setResubmitOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close"
            onKeyDown={(e) => e.key === 'Enter' && setResubmitOpen(false)}
          />
          <div
            className="relative z-10 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Resubmit Batch
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              All companies must have approved runs before resubmitting.
            </p>

            {readinessLoading ? (
              <div className="mt-4">
                <TableSkeleton rows={3} cols={2} />
              </div>
            ) : readiness ? (
              <div className="mt-4 max-h-60 overflow-y-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700">
                      <th className="px-3 py-2">Company</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readiness.map((entry) => (
                      <tr
                        key={entry.companyId}
                        className="border-b border-gray-100 dark:border-gray-700"
                      >
                        <td className="px-3 py-2">{entry.companyName}</td>
                        <td className="px-3 py-2">
                          {entry.runStatus ? (
                            <StatusBadge status={entry.runStatus} />
                          ) : (
                            <span className="text-gray-400">No run</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setResubmitOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!allReady} loading={resubmitMut.isPending}
                onClick={handleResubmit}
              >
                {resubmitMut.isPending ? '...' : 'Resubmit'}
              </Button>
            </div>
            {!allReady && readiness && !readinessLoading && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                {readinessTotal - readinessApproved} company(ies) not ready. Approve all runs first.
              </p>
            )}
          </div>
        </div>
      )}
    </PageShell>
  )
}

BatchDetailPage.displayName = 'BatchDetailPage'
